import type { ListItemButtonProps } from '@mui/material';
import { List, ListItemButton, ListItemText, Paper } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import { isProduction } from '@constants/env';
import { ManageAccountMenuRoute } from '@constants/manage-account';

import { useSession } from '@hooks/useSession';

import type { Profile } from '@models/session';

import { isAccountAdmin, isPatient } from '@utils/role';

const messages = defineMessages({
  profileSecurity: {
    id: 'manageAccount.profileAndSecurity',
    defaultMessage: 'Profile and Security"',
  },
  securitySub: {
    id: 'manageAccount.securitySubtitle',
    defaultMessage: 'Update your name, password, and more',
  },
  paymentBilling: {
    id: 'manageAccount.paymentBilling',
    defaultMessage: 'Payment and Billing',
  },
  paymentSub: {
    id: 'manageAccount.paymentSubtitle',
    defaultMessage: 'Manage payment and billing information',
  },
  consents: {
    id: 'manageAccount.consents',
    defaultMessage: 'Consents',
  },
  consentSub: {
    id: 'manageAccount.consentSubtitle',
    defaultMessage: 'Add or revoke your consent',
  },
  accessManagement: {
    id: 'manageAccount.accessManagement',
    defaultMessage: 'Access Management',
  },
  accessSub: {
    id: 'manageAccount.accessManagementSubtitle',
    defaultMessage: 'Manage users and permissions',
  },
  notifications: {
    id: 'manageAccount.notifications',
    defaultMessage: 'Notifications',
  },
  notificationSub: {
    id: 'manageAccount.notificationSubtitle',
    defaultMessage: 'Choose which notifications you receive',
  },
  alerts: {
    id: 'manageAccount.alerts',
    defaultMessage: 'Alerts',
  },
  alertSub: {
    id: 'manageAccount.alertSubtitle',
    defaultMessage: 'Manage how health metric alerts are delivered to you',
  },
  settings: {
    id: 'manageAccount.settings',
    defaultMessage: 'Settings',
  },
  plan: {
    id: 'manageAccount.plan',
    defaultMessage: 'Plan',
  },
  planSub: {
    id: 'manageAccount.planSubtitle',
    defaultMessage: 'View your plan features and limits',
  },
  appearance: {
    id: 'manageAccount.appearance',
    defaultMessage: 'appearance',
  },
  appearanceSub: {
    id: 'manageAccount.appearanceSubtitle',
    defaultMessage: 'Change appearance and theme settings',
  },
});

interface ManageAccountMenuProps {
  user: Profile | null;
  selectedItem?: string;
  onSelect?: (route: ManageAccountMenuRoute) => void;
}

export default function ManageAccountMenu({
  user,
  selectedItem,
  onSelect,
}: ManageAccountMenuProps) {
  const { formatMessage } = useIntl();
  const {
    state: { loggedInProfile, isVitall },
  } = useSession();

  const listItemButtonProps: ListItemButtonProps = {
    divider: true,
  };
  const secondaryAction = (
    <Icon color="text.primary" name="chevron-down" rotation={-90} type="fas" />
  );

  const handleSelection = (tab: ManageAccountMenuRoute) => onSelect?.(tab);

  if (!user) {
    return null;
  }
  return (
    <Paper>
      <List>
        <ListItemButton
          {...listItemButtonProps}
          onClick={() =>
            handleSelection(ManageAccountMenuRoute.ProfileSecurity)
          }
          selected={selectedItem === ManageAccountMenuRoute.ProfileSecurity}
        >
          <ListItemText
            primary={formatMessage(messages.profileSecurity)}
            secondary={formatMessage(messages.securitySub)}
          />
          {secondaryAction}
        </ListItemButton>

        {/* Only display Payment section if active profile is an account admin and is the logged in user, and is not a managed account */}
        {isAccountAdmin(user) &&
          loggedInProfile?.user_id === user.user_id &&
          !user.account?.isManaged && (
            <ListItemButton
              {...listItemButtonProps}
              onClick={() =>
                handleSelection(ManageAccountMenuRoute.PaymentBilling)
              }
              selected={selectedItem === ManageAccountMenuRoute.PaymentBilling}
            >
              <ListItemText
                primary={formatMessage(messages.paymentBilling)}
                secondary={formatMessage(messages.paymentSub)}
              />
              {secondaryAction}
            </ListItemButton>
          )}

        <ListItemButton
          {...listItemButtonProps}
          onClick={() => handleSelection(ManageAccountMenuRoute.Consents)}
          selected={selectedItem === ManageAccountMenuRoute.Consents}
        >
          <ListItemText
            primary={formatMessage(messages.consents)}
            secondary={formatMessage(messages.consentSub)}
          />
          {secondaryAction}
        </ListItemButton>

        {isPatient(user) && (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() =>
              handleSelection(ManageAccountMenuRoute.AccessManagement)
            }
            selected={selectedItem === ManageAccountMenuRoute.AccessManagement}
          >
            <ListItemText
              primary={formatMessage(messages.accessManagement)}
              secondary={formatMessage(messages.accessSub)}
            />
            {secondaryAction}
          </ListItemButton>
        )}
        <ListItemButton
          {...listItemButtonProps}
          onClick={() => handleSelection(ManageAccountMenuRoute.Notifications)}
          selected={selectedItem === ManageAccountMenuRoute.Notifications}
        >
          <ListItemText
            primary={formatMessage(messages.notifications)}
            secondary={formatMessage(messages.notificationSub)}
          />
          {secondaryAction}
        </ListItemButton>

        {!isVitall && (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => handleSelection(ManageAccountMenuRoute.Alerts)}
            selected={selectedItem === ManageAccountMenuRoute.Alerts}
          >
            <ListItemText
              primary={formatMessage(messages.alerts)}
              secondary={formatMessage(messages.alertSub)}
            />
            {secondaryAction}
          </ListItemButton>
        )}
        {user.account?.parent_id && (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => handleSelection(ManageAccountMenuRoute.Plan)}
            selected={selectedItem === ManageAccountMenuRoute.Plan}
          >
            <ListItemText
              primary={formatMessage(messages.plan)}
              secondary={formatMessage(messages.planSub)}
            />
            {secondaryAction}
          </ListItemButton>
        )}
        {!isProduction && (
          <ListItemButton
            {...listItemButtonProps}
            onClick={() => handleSelection(ManageAccountMenuRoute.Appearance)}
            selected={selectedItem === ManageAccountMenuRoute.Appearance}
          >
            <ListItemText primary={formatMessage(messages.appearance)} />
            {secondaryAction}
          </ListItemButton>
        )}
        <ListItemButton
          onClick={() => handleSelection(ManageAccountMenuRoute.Settings)}
          selected={selectedItem === ManageAccountMenuRoute.Settings}
        >
          <ListItemText primary={formatMessage(messages.settings)} />
          {secondaryAction}
        </ListItemButton>
      </List>
    </Paper>
  );
}
